import { useEffect } from 'react';

/**
 * SchemaMarkup Component
 * Injects JSON-LD structured data into the page head for SEO
 * Supports NewsArticle with AggregateRating schema
 */
const SchemaMarkup = ({
  type = 'NewsArticle',
  title = '',
  description = '',
  image = '',
  datePublished = '',
  dateModified = '',
  author = {
    '@type': 'Organization',
    name: 'Alberta Driving Class 7 Test Prep',
    url: typeof window !== 'undefined' ? window.location.origin : '',
  },
  aggregateRating = null,
  url = '',
  content = '',
}) => {
  useEffect(() => {
    // Create the schema object
    let schema = {};

    if (type === 'NewsArticle') {
      schema = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: title,
        description: description || title,
        image: image,
        datePublished: datePublished,
        dateModified: dateModified || datePublished,
        author: author,
        url: url || (typeof window !== 'undefined' ? window.location.href : ''),
        articleBody: content,
        publisher: {
          '@type': 'Organization',
          name: 'Alberta Driving Class 7 Test Prep',
          logo: {
            '@type': 'ImageObject',
            url:
              typeof window !== 'undefined'
                ? `${window.location.origin}/assets/logo.png`
                : '',
          },
        },
      };

      // Add aggregate rating if available
      if (aggregateRating && aggregateRating.ratingValue > 0) {
        schema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: aggregateRating.ratingValue,
          ratingCount: aggregateRating.ratingCount,
          bestRating: 5,
          worstRating: 1,
        };
      }
    }

    // Create script tag
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.async = true;

    // Add to head
    document.head.appendChild(script);

    // Cleanup
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [type, title, description, image, datePublished, dateModified, author, aggregateRating, url, content]);

  return null;
};

/**
 * BreadcrumbSchema Component
 * Creates breadcrumb structured data for better navigation in SERPs
 */
export const BreadcrumbSchema = ({ items = [] }) => {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.async = true;

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [items]);

  return null;
};

/**
 * OrganizationSchema Component
 * Creates organization structured data for the site
 */
export const OrganizationSchema = () => {
  useEffect(() => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Alberta Driving Class 7 Test Prep',
      url: baseUrl,
      logo: `${baseUrl}/assets/logo.png`,
      description: 'Comprehensive Alberta Class 7 driving test preparation platform with practice questions, news, and resources.',
      sameAs: [
        // Add your social media URLs here
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        availableLanguage: ['en'],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.async = true;

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return null;
};

/**
 * AggregateRatingSchema Component
 * Standalone aggregate rating schema component
 */
export const AggregateRatingSchema = ({ 
  ratingValue = 0, 
  ratingCount = 0,
  itemName = '',
  itemUrl = '' 
}) => {
  useEffect(() => {
    if (ratingValue <= 0 || ratingCount <= 0) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      ratingValue: parseFloat(ratingValue).toFixed(1),
      ratingCount: parseInt(ratingCount),
      bestRating: 5,
      worstRating: 1,
      itemReviewed: {
        '@type': 'NewsArticle',
        name: itemName,
        url: itemUrl,
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.async = true;

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [ratingValue, ratingCount, itemName, itemUrl]);

  return null;
};

/**
 * NewsCollectionSchema Component
 * For a collection of news articles (like on the news listing page)
 */
export const NewsCollectionSchema = ({ articles = [] }) => {
  useEffect(() => {
    if (!articles || articles.length === 0) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Latest Driving News & Updates',
      description: 'Stay informed with the latest tips, regulations, and driving knowledge',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: articles
          .slice(0, 100) // Schema.org best practice: max 100 items
          .map((article, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: article.url || '',
            name: article.title || '',
            description: article.description || '',
            image: article.image || '',
            datePublished: article.datePublished || '',
            aggregateRating: article.aggregateRating
              ? {
                  '@type': 'AggregateRating',
                  ratingValue: article.aggregateRating.ratingValue,
                  ratingCount: article.aggregateRating.ratingCount,
                  bestRating: 5,
                  worstRating: 1,
                }
              : undefined,
          }))
          .filter((item) => item.url && item.name), // Remove invalid items
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.async = true;

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [articles]);

  return null;
};

export default SchemaMarkup;
